import { ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection, LOCALE_ID, isDevMode } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import { MessageService, ConfirmationService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import { routes } from '@app/app.routes';
import { jwtInterceptor } from '@app/core/auth/interceptors/jwt-interceptor';
import { httpErrorInterceptor } from '@app/core/auth/interceptors/http-error.interceptor';
import { SessionActivityService } from '@app/core/auth/session-activity.service';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAppInit, providePwaUpdateInit } from '@app/core/initializers/app-initializer';
import { provideEmbedSessionBridge } from '@app/core/auth/embed-session-bridge';
import { getInitialLang, localeIdFor } from '@app/core/i18n/initial-language';
import { primeNgTranslationFor } from '@app/core/i18n/primeng-translations';

// Type for extended semantic colors (PrimeNG theme extension)
// Using 'as any' to bypass TypeScript checking for custom semantic colors
type ExtendedColorScheme = Record<string, any>;

const AuraSky = definePreset(Aura, {
  semantic: {
    // ===== PRIMARY COLORS (Brand) =====
    primary: {
      50: '#eff4f8',
      100: '#cfdee9',
      200: '#b0c7da',
      300: '#90b1cc',
      400: '#709bbd',
      500: '#5084af', // Brand base color
      600: '#426c8f',
      700: '#33546f',
      800: '#253c4f',
      900: '#162430',
      950: '#070c10'
    },

    // ===== STATUS COLORS (Entity States) =====
    // RED: New/Creation - attention-grabbing, urgent
    statusNew: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444', // Main red
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a'
    },

    // AMBER: Modified - change indicator, not urgent
    statusModified: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b', // Main amber
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03'
    },

    // INDIGO: Related - distinct from primary, not status
    statusRelated: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1', // Main indigo
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
      950: '#1e1b4b'
    },

    // ===== ACTION COLORS (User Flags) =====
    actionThreat: {
      light: '#dc2626', // red-600
      dark: '#ef4444'   // red-500
    },
    actionOpportunity: {
      light: '#059669', // green-600
      dark: '#10b981'   // green-500
    },
    actionFavorite: {
      light: '#eab308', // yellow-500
      dark: '#fbbf24'   // yellow-400
    },
    actionInteresting: {
      light: '#3b82f6', // blue-500
      dark: '#60a5fa'   // blue-400
    },
    actionNotInteresting: {
      light: '#9ca3af', // gray-400
      dark: '#6b7280'   // gray-500
    },

    // ===== FEEDBACK COLORS (System Messages) =====
    feedbackSuccess: {
      light: '#10b981', // green-500
      dark: '#34d399'   // green-400
    },
    feedbackWarning: {
      light: '#f59e0b', // amber-500
      dark: '#fbbf24'   // amber-400
    },
    feedbackError: {
      light: '#ef4444', // red-500
      dark: '#f87171'   // red-400
    },
    feedbackInfo: {
      light: '#3b82f6', // blue-500
      dark: '#60a5fa'   // blue-400
    },

    // ===== SIGNAL ACCENT (Sentinel Design System — Cyan) =====
    // Distinct from all status/action/feedback palettes.
    // Used for: active selection, live-data highlights, focus rings in dark mode.
    // Never used decoratively — signals system activity.
    signalAccent: {
      200: '#A5F3FC', // Light mode subtle accent
      400: '#22D3EE', // Dark mode hover glow
      500: '#06B6D4', // Primary signal — active selection, system signal
      600: '#0891B2', // Hover pressed
    },

    colorScheme: {
      light: {
        // ===== PRIMARY =====
        primary: {
          color: '{semantic.primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{semantic.primary.800}',
          activeColor: '{semantic.primary.900}'
        },
        highlight: {
          background: '{semantic.primary.50}',
          focusBackground: '{semantic.primary.100}',
          color: '{semantic.primary.900}',
          focusColor: '{semantic.primary.900}'
        },

        // ===== STATUS: NEW =====
        statusNew: {
          background: '{semantic.statusNew.50}',
          borderColor: '{semantic.statusNew.500}',
          color: '{semantic.statusNew.700}',
          hoverBackground: '{semantic.statusNew.100}',
          activeBackground: '{semantic.statusNew.200}'
        },

        // ===== STATUS: MODIFIED =====
        statusModified: {
          background: '{semantic.statusModified.50}',
          borderColor: '{semantic.statusModified.500}',
          color: '{semantic.statusModified.700}',
          hoverBackground: '{semantic.statusModified.100}',
          activeBackground: '{semantic.statusModified.200}'
        },

        // ===== STATUS: RELATED =====
        statusRelated: {
          background: '{semantic.statusRelated.50}',
          borderColor: '{semantic.statusRelated.500}',
          color: '{semantic.statusRelated.700}',
          hoverBackground: '{semantic.statusRelated.100}',
          activeBackground: '{semantic.statusRelated.200}'
        },

        // ===== ACTIONS =====
        actionThreat: {
          color: '{semantic.actionThreat.light}'
        },
        actionOpportunity: {
          color: '{semantic.actionOpportunity.light}'
        },
        actionFavorite: {
          color: '{semantic.actionFavorite.light}'
        },
        actionInteresting: {
          color: '{semantic.actionInteresting.light}'
        },
        actionNotInteresting: {
          color: '{semantic.actionNotInteresting.light}'
        },

        // ===== FEEDBACK =====
        feedbackSuccess: {
          color: '{semantic.feedbackSuccess.light}'
        },
        feedbackWarning: {
          color: '{semantic.feedbackWarning.light}'
        },
        feedbackError: {
          color: '{semantic.feedbackError.light}'
        },
        feedbackInfo: {
          color: '{semantic.feedbackInfo.light}'
        }
      },

      dark: {
        // ===== PRIMARY =====
        primary: {
          color: '{semantic.primary.50}',
          contrastColor: '{semantic.primary.900}',
          hoverColor: '{semantic.primary.100}',
          activeColor: '{semantic.primary.200}'
        },
        highlight: {
          background: 'rgba(65,107,141,0.16)',
          focusBackground: 'rgba(65,107,141,0.24)',
          color: '#fff',
          focusColor: '#fff'
        },

        // ===== STATUS: NEW =====
        statusNew: {
          background: '{semantic.statusNew.900}',
          borderColor: '{semantic.statusNew.500}',
          color: '{semantic.statusNew.100}',
          hoverBackground: '{semantic.statusNew.800}',
          activeBackground: '{semantic.statusNew.700}'
        },

        // ===== STATUS: MODIFIED =====
        statusModified: {
          background: '{semantic.statusModified.900}',
          borderColor: '{semantic.statusModified.500}',
          color: '{semantic.statusModified.100}',
          hoverBackground: '{semantic.statusModified.800}',
          activeBackground: '{semantic.statusModified.700}'
        },

        // ===== STATUS: RELATED =====
        statusRelated: {
          background: '{semantic.statusRelated.900}',
          borderColor: '{semantic.statusRelated.500}',
          color: '{semantic.statusRelated.100}',
          hoverBackground: '{semantic.statusRelated.800}',
          activeBackground: '{semantic.statusRelated.700}'
        },

        // ===== ACTIONS =====
        actionThreat: {
          color: '{semantic.actionThreat.dark}'
        },
        actionOpportunity: {
          color: '{semantic.actionOpportunity.dark}'
        },
        actionFavorite: {
          color: '{semantic.actionFavorite.dark}'
        },
        actionInteresting: {
          color: '{semantic.actionInteresting.dark}'
        },
        actionNotInteresting: {
          color: '{semantic.actionNotInteresting.dark}'
        },

        // ===== FEEDBACK =====
        feedbackSuccess: {
          color: '{semantic.feedbackSuccess.dark}'
        },
        feedbackWarning: {
          color: '{semantic.feedbackWarning.dark}'
        },
        feedbackError: {
          color: '{semantic.feedbackError.dark}'
        },
        feedbackInfo: {
          color: '{semantic.feedbackInfo.dark}'
        }
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useFactory: () => localeIdFor(getInitialLang()) },
    provideRouter(routes),
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    MessageService,
    ConfirmationService,
    SessionActivityService, // Initialize session activity monitoring
    provideAppInit(),
    provideEmbedSessionBridge(),
    providePwaUpdateInit(), // Initialize PWA update checking
    providePrimeNG({
        theme: {
            preset: AuraSky,
            options: {
                darkModeSelector: '.dark',
                cssLayer: {
                    name: 'primeng',
                    order: 'base, theme, primeng'
                }
            }
        },
        ripple: true,
        translation: primeNgTranslationFor(getInitialLang())
    }),
    provideHttpClient(withInterceptors([jwtInterceptor, httpErrorInterceptor])),
    provideTranslateService({
      fallbackLang: 'es'
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }), provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
          })
  ]
};
